import Axios from 'axios'
import { isAxiosError } from '../../Helpers/axiosTypes'
import { isC1ApiError } from '../../Helpers/c1ApiTypes'
import { canHaveCustomProperties } from '../../Helpers/canHaveCustomProperties'
import { User } from '../../Permissions/AuthenticatedUser.context'

export interface TenantSummary { id: number, name: string }
export interface C1AuthSuccess { type: 'success', user: User }
export interface C1AuthTenantConflict { type: 'tenantConflict', tenants: TenantSummary[] }
export interface C1LoginData {
  userId: string,
  password: string,
  tenantId: number | null,
  locale: string,
  selectedLanguage: string,
}
interface C1AuthParams {
  auth: 'c1' | 'saml',
  tenantId?: number,
  userId?: string,
}

const TenantConflictHttpStatus = 409

function isTenantConflictMessage(parsedMessage: unknown): parsedMessage is TenantSummary[] {
  return Array.isArray(parsedMessage) && parsedMessage.every(item => canHaveCustomProperties(item) && typeof item.id === 'number' && typeof item.name === 'string')
}

export type LoginMethod = 'classic' | 'external' | 'choice'
interface C1LoginMethod { authenticationMethod: LoginMethod, hasClassicIdp: boolean }

export async function getLoginMethod(email: string): Promise<C1LoginMethod> {
  const resp = await Axios.post<C1LoginMethod>('api/rest/iam/auth/method/user', { email }, { /* params */ })
  return resp.data
}

export async function login(data: C1LoginData): Promise<C1AuthSuccess | C1AuthTenantConflict> {
  try {
    const params: C1AuthParams = { auth: 'c1' }
    const resp = await Axios.post<User>('/api/rest/authorize', data, { params })
    return { type: 'success', user: resp.data }
  } catch (e) {
    // bubble up the error if it's not the expected tenant conflict response
    if (!isAxiosError(e)) throw e
    else if (!e.response) throw e
    else if (!isC1ApiError(e.response.data)) throw e
    else if (e.response.status !== TenantConflictHttpStatus) throw e
    const parsedMessage = JSON.parse(e.response.data.errorMessage)
    if (!isTenantConflictMessage(parsedMessage)) throw e
    return { type: 'tenantConflict', tenants: parsedMessage }
  }
}

const SSO_REDIRECT_RESPONSE_STATUS = 278

export interface SSOAuthRedirect {
  type: 'redirect',
  redirectTo: string,
}

export async function singleSignOn(email: string, tenantId?: number): Promise<C1AuthSuccess | C1AuthTenantConflict | SSOAuthRedirect> {
  const params: C1AuthParams = { userId: email, auth: 'saml' }
  if (tenantId !== undefined) {
    params.tenantId = tenantId
  }
  try {
    const response = await Axios.get<User>('/api/rest/authorize', { params })
    return response.status === SSO_REDIRECT_RESPONSE_STATUS
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TODO: remove type assertion
      ? { type: 'redirect', redirectTo: response.headers.location as string }
      : { type: 'success', user: response.data }
  } catch (e) {
    // bubble up the error if it's not the expected tenant conflict response
    if (!isAxiosError(e)) throw e
    else if (!e.response) throw e
    else if (!isC1ApiError(e.response.data)) throw e
    else if (e.response.status !== TenantConflictHttpStatus) throw e
    const parsedMessage = JSON.parse(e.response.data.errorMessage)
    if (!isTenantConflictMessage(parsedMessage)) throw e
    return { type: 'tenantConflict', tenants: parsedMessage }
  }
}

export interface PasswordResetEmailSent {
  /** The email to which the forgot password email was sent */
  email: string,
  /** The expiration timestamp of the forgot password email */
  expiration: number,
}

export const sendPasswordResetEmail = (email: string): Promise<PasswordResetEmailSent> => Axios
  .post<PasswordResetEmailSent>('/api/password/reset', { email })
  .then(resp => resp.data)

export function redirectToForgeRockLogin(forgeRockURL: string, email: string, enableWebMonitoring: boolean): void {
  location.href = `${forgeRockURL}&email=${encodeURIComponent(email)}&enableWebMonitoring=${enableWebMonitoring}`
}
